import { api } from "./base";

export interface Task {
  _id: string;
  projectId: string;
  title: string;
  description?: string;
  type: "TASK" | "BUG" | "FEATURE" | "IMPROVEMENT";
  status: "TODO" | "IN_PROGRESS" | "IN_REVIEW" | "DONE";
  priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  assigneeId?: string;
  reporterId: string;
  createdAt: string;
  updatedAt: string;
  dueDate?: string;
  estimatedHours?: number;
  labels?: string[];
}

export interface TaskComment {
  _id: string;
  taskId: string;
  userId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskData {
  projectId: string;
  title: string;
  description?: string;
  type: Task["type"];
  priority: Task["priority"];
  assigneeId?: string;
  dueDate?: string;
  estimatedHours?: number;
  labels?: string[];
}

export interface UpdateTaskData {
  title?: string;
  description?: string;
  type?: Task["type"];
  status?: Task["status"];
  priority?: Task["priority"];
  assigneeId?: string;
  dueDate?: string;
  estimatedHours?: number;
  labels?: string[];
}

export interface ListTasksParams {
  projectId?: string;
  assigneeId?: string;
  reporterId?: string;
  status?: Task["status"];
  type?: Task["type"];
  priority?: Task["priority"];
  search?: string;
  limit?: number;
  offset?: number;
}

export const tasksApi = {
  list: async (params?: ListTasksParams) => {
    const response = await api.get<{ tasks: Task[]; total: number }>("/tasks", {
      params,
    });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<Task>(`/tasks/${id}`);
    return response.data;
  },

  create: async (data: CreateTaskData) => {
    const response = await api.post<Task>("/tasks", data);
    return response.data;
  },

  update: async (id: string, data: UpdateTaskData) => {
    const response = await api.patch<Task>(`/tasks/${id}`, data);
    return response.data;
  },

  // Task Comments
  listComments: async (taskId: string) => {
    const response = await api.get<TaskComment[]>(`/tasks/${taskId}/comments`);
    return response.data;
  },

  createComment: async (taskId: string, content: string) => {
    const response = await api.post<TaskComment>(`/tasks/${taskId}/comments`, {
      content,
    });
    return response.data;
  },

  deleteComment: async (taskId: string, commentId: string) => {
    await api.delete(`/tasks/${taskId}/comments/${commentId}`);
  },

  // Bulk update task status (for drag and drop)
  updateStatus: async (taskId: string, status: Task["status"]) => {
    const response = await api.patch<Task>(`/tasks/${taskId}`, { status });
    return response.data;
  },
};
